/**
 * A LinkedIn agent draft: a `social_posts` row (`source_kind = 'linkedin_agent'`, `status = 'draft'`) whose
 * `quality.linkedinAgent` carries what the review deck needs: the sources the post was written from, the take it
 * proposes, and the review record. Ruling: `docs/planning/brand-agent-2026-10-30/45-BOARD-RULING-SYSTEM.md`.
 *
 * Pure: parsing and shaping only. Reading `quality` is defensive because it is untyped jsonb.
 */
import {
  type PostCheckInput,
  type PostCheckSource,
  VOICE_SAMPLES_NEEDED,
} from '@/lib/content-doctrine/linkedin-post-check';

export const LINKEDIN_AGENT_SOURCE_KIND = 'linkedin_agent';
/** The author's own product names: naming one in a post reads as promotion (ruling 9). */
export const OWN_PRODUCT_NAMES = ['Mora'];

/** The author's specialty, as word stems. A post may use them only when its article does: the model forced
 *  "orchestration" into three of three posts on 2026-10-31, and "AI agents" into a crypto theft and a satellite
 *  launch, none of them about either. */
export const SPECIALTY_TERMS = ['orchestrat', 'agent'];

/* Newsletter and ad tracking parameters. A post that attaches its article must not carry someone else's campaign
   tags to the person's audience (a Fast Company link arrived with utm_*, partner and campaign_date, 2026-11-01). */
const TRACKING_PARAM =
  /^(utm_\w+|fbclid|gclid|mc_cid|mc_eid|partner|position|campaign_date|ref|cmpid|smid)$/i;

/** The article URL without tracking parameters; returns the input unchanged when it is not a URL. */
export function cleanArticleUrl(url: string): string {
  try {
    const parsed = new URL(url);
    for (const key of [...parsed.searchParams.keys()])
      if (TRACKING_PARAM.test(key)) parsed.searchParams.delete(key);
    return parsed.toString();
  } catch {
    return url;
  }
}

/** The editor's finding that the story is outside the person's field. A rewrite cannot fix it; only a different
 *  story can, so the deck offers Skip instead of a rewrite. */
export const OFF_LANE_LABEL = 'Not a story for your field';

export interface LinkedInAgentSource extends PostCheckSource {
  readonly url?: string;
  /** The article's headline and hero image, for the article card under the post. Absent on drafts before 2026-11-01. */
  readonly title?: string;
  readonly imageUrl?: string | null;
}

export interface LinkedInAgentTake {
  readonly text: string;
  readonly status: 'proposed' | 'confirmed';
}

export interface LinkedInAgentReview {
  readonly decision: 'approved';
  readonly at: string;
  /** Hash of the exact text approved, so an edit after approval is visibly a different version. */
  readonly version: string;
  readonly msOnCard: number;
  readonly takeAdopted: boolean;
}

/** The editor's read of one exact version of the post. It only exists when the editor found nothing to fix. */
export interface LinkedInAgentEditorNote {
  readonly label: string;
  readonly quote: string;
  readonly fix: string;
}

export interface LinkedInAgentEditorRead {
  readonly version: string;
  readonly at: string;
  /** The editor's remaining notes on that version; empty when it found nothing. */
  readonly notes: LinkedInAgentEditorNote[];
}

/** The text a rewrite replaced, kept so the person can go back to it. Restoring swaps it with the current text. */
export interface LinkedInAgentPreviousVersion {
  readonly content: string;
  readonly take: LinkedInAgentTake | null;
  readonly editor: LinkedInAgentEditorRead | null;
  readonly at: string;
}

export interface LinkedInAgentMeta {
  readonly sources: LinkedInAgentSource[];
  readonly take: LinkedInAgentTake | null;
  readonly developing: boolean;
  readonly seed: boolean;
  readonly review: LinkedInAgentReview | null;
  readonly editor: LinkedInAgentEditorRead | null;
  /** The voice the person had set when this draft was written; null for drafts written before they set one. */
  readonly voiceLabel?: string | null;
  readonly previous?: LinkedInAgentPreviousVersion | null;
}

export interface LinkedInAgentDraft {
  readonly id: string;
  readonly content: string;
  readonly createdAt: string;
  readonly approvedAt: string | null;
  /** When the post is planned on the calendar; planning never publishes. */
  readonly scheduledAt: string | null;
  /** The workspace the post lives in. The calendar shows one workspace at a time. */
  readonly brandKitId: string | null;
  /** 'draft' until it is scheduled to publish; 'scheduled' once it will go out at `scheduledAt`. */
  readonly status?: string;
  readonly sourceUrl: string | null;
  readonly meta: LinkedInAgentMeta;
}

type Json = unknown;

const isRecord = (value: Json): value is Record<string, Json> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const str = (value: Json): string | undefined => (typeof value === 'string' ? value : undefined);

function parseSource(value: Json): LinkedInAgentSource | null {
  if (!isRecord(value)) return null;
  const name = str(value.name);
  const text = str(value.text);
  if (!name || text === undefined) return null;
  return {
    name,
    text,
    url: str(value.url),
    partial: value.partial === true,
    publishedAt: str(value.publishedAt) ?? null,
    title: str(value.title),
    imageUrl: str(value.imageUrl) ?? null,
  };
}

function parseTake(value: Json): LinkedInAgentTake | null {
  if (!isRecord(value)) return null;
  const text = str(value.text);
  if (!text) return null;
  return { text, status: value.status === 'confirmed' ? 'confirmed' : 'proposed' };
}

function parseReview(value: Json): LinkedInAgentReview | null {
  if (!isRecord(value) || value.decision !== 'approved') return null;
  return {
    decision: 'approved',
    at: str(value.at) ?? '',
    version: str(value.version) ?? '',
    msOnCard: typeof value.msOnCard === 'number' ? value.msOnCard : 0,
    takeAdopted: value.takeAdopted === true,
  };
}

function parseEditor(value: Json): LinkedInAgentEditorRead | null {
  if (!isRecord(value)) return null;
  const version = str(value.version);
  if (!version) return null;
  const notes = Array.isArray(value.notes)
    ? value.notes.flatMap((n) =>
        isRecord(n) && str(n.label)
          ? [{ label: str(n.label) ?? '', quote: str(n.quote) ?? '', fix: str(n.fix) ?? '' }]
          : [],
      )
    : [];
  return { version, at: str(value.at) ?? '', notes };
}

function parsePrevious(value: Json): LinkedInAgentPreviousVersion | null {
  if (!isRecord(value)) return null;
  const content = str(value.content);
  if (!content) return null;
  return {
    content,
    take: parseTake(value.take),
    editor: parseEditor(value.editor),
    at: str(value.at) ?? '',
  };
}

export function parseLinkedInAgentMeta(quality: Json): LinkedInAgentMeta {
  const block = isRecord(quality) && isRecord(quality.linkedinAgent) ? quality.linkedinAgent : {};
  const sources = Array.isArray(block.sources)
    ? block.sources.map(parseSource).filter((s): s is LinkedInAgentSource => s !== null)
    : [];
  return {
    sources,
    take: parseTake(block.take),
    developing: block.developing === true,
    seed: block.seed === true,
    review: parseReview(block.review),
    editor: parseEditor(block.editor),
    voiceLabel: isRecord(block.voice) ? (str(block.voice.label) ?? null) : null,
    previous: parsePrevious(block.previous),
  };
}

/** The `quality` value with `linkedinAgent` replaced, keeping any other keys a different writer owns. */
export function withLinkedInAgentMeta(
  quality: Json,
  patch: Partial<LinkedInAgentMeta>,
): Record<string, Json> {
  const base = isRecord(quality) ? quality : {};
  const current = isRecord(base.linkedinAgent) ? base.linkedinAgent : {};
  return { ...base, linkedinAgent: { ...current, ...patch } };
}

/** Views recorded for a published post. LinkedIn does not share them for personal posts, so the person enters them. */
export function agentPostViews(quality: Json): number | null {
  const block = isRecord(quality) && isRecord(quality.linkedinAgent) ? quality.linkedinAgent : null;
  const results = block && isRecord(block.results) ? block.results : null;
  return typeof results?.impressions === 'number' ? results.impressions : null;
}

/** The `quality` value with a person-entered view count; null removes it. */
export function withAgentPostViews(quality: Json, views: number | null): Record<string, Json> {
  const base = isRecord(quality) ? quality : {};
  const { results: _dropped, ...current } = isRecord(base.linkedinAgent) ? base.linkedinAgent : {};
  return {
    ...base,
    linkedinAgent:
      views === null
        ? current
        : { ...current, results: { impressions: views, enteredBy: 'person' } },
  };
}

export function toCheckInput(
  content: string,
  meta: LinkedInAgentMeta,
  extra: { pastPosts?: string[]; voiceSampleCount?: number; doNotName?: readonly string[] } = {},
): PostCheckInput {
  return {
    text: content,
    sources: meta.sources,
    take: meta.take,
    doNotName: extra.doNotName,
    ownProductNames: OWN_PRODUCT_NAMES,
    specialtyTerms: SPECIALTY_TERMS,
    editorRead: meta.editor?.version === versionOf(content),
    editorNotes: meta.editor?.notes ?? [],
    developing: meta.developing,
    fromOwnNote: meta.seed,
    pastPosts: extra.pastPosts,
    // A draft written in the voice the person set reads as learned; the count is the check's own threshold.
    voiceSampleCount: extra.voiceSampleCount ?? (meta.voiceLabel ? VOICE_SAMPLES_NEEDED : 0),
  };
}

/* News commentary decays: a draft about a story is worth most within two or three days. A starting value, to be
   measured once post analytics exist (docs/planning/brand-agent-2026-10-30/55-AUTONOMY-DESIGN.md, 2.1). */
const NEWS_SHELF_HOURS = 72;
const DEVELOPING_SHELF_HOURS = 48;
const OWN_IDEA_SHELF_HOURS = 14 * 24;

/** When a draft stops being worth posting: its story's publish time (or the draft's own) plus its shelf life. */
export function freshUntil(draft: {
  createdAt: string;
  meta: Pick<LinkedInAgentMeta, 'sources' | 'developing' | 'seed'>;
}): Date {
  const source = draft.meta.sources[0];
  const hours = draft.meta.seed
    ? OWN_IDEA_SHELF_HOURS
    : draft.meta.developing
      ? DEVELOPING_SHELF_HOURS
      : NEWS_SHELF_HOURS;
  const from = Date.parse(source?.publishedAt ?? '') || Date.parse(draft.createdAt) || Date.now();
  return new Date(from + hours * 3_600_000);
}

/**
 * The image a tile shows for a post: its own media, or, for an agent post with none, its article's. The article card
 * is what LinkedIn will show, so a tile without it misrepresents the post (founder, 4 Nov 2026).
 */
export function postTileImage(post: {
  media?: string | null;
  source_kind?: string | null;
  source_url?: string | null;
  content?: string | null;
  quality?: Json;
  article_image?: string | null;
}): string | null {
  return post.media ?? post.article_image ?? linkedInArticleCard(post)?.imageUrl ?? null;
}

/** The article card a LinkedIn agent post carries, as LinkedIn draws it: headline, domain and image. */
export interface LinkedInArticleCard {
  title?: string;
  domain: string;
  imageUrl?: string | null;
}

/**
 * The article card for a post, or null when it has none. Only LinkedIn agent posts carry one: the publisher attaches
 * their source article (`source_url`) when no link is in the text. Shared by the review deck and the post inspector,
 * so the calendar shows the same post the deck approved.
 */
export function linkedInArticleCard(post: {
  source_kind?: string | null;
  source_url?: string | null;
  content?: string | null;
  quality?: Json;
}): LinkedInArticleCard | null {
  if (post.source_kind !== LINKEDIN_AGENT_SOURCE_KIND) return null;
  if (post.content && /https?:\/\//.test(post.content)) return null;
  const source = parseLinkedInAgentMeta(post.quality).sources.find((s) => s.url);
  const url = post.source_url ?? source?.url;
  if (!url) return null;
  try {
    return {
      title: source?.title,
      domain: new URL(url).hostname.replace(/^www\./, ''),
      imageUrl: source?.imageUrl ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * How the agent publishes (personal_brand_agent_settings.posting_mode):
 * 'ask' waits for the person to schedule; 'approved' schedules what they approve; 'auto' also schedules drafts
 * that pass every check with nothing flagged, far enough ahead that the person can stop them.
 */
export type PostingMode = 'ask' | 'approved' | 'auto';

/** Why a draft was skipped, offered as one optional tap. The weekly run learns from these. */
export const SKIP_REASONS = [
  { id: 'not_my_field', label: 'Not my field' },
  { id: 'wrong_view', label: 'Wrong view' },
  { id: 'weak_story', label: 'Weak story' },
  { id: 'already_covered', label: 'Already covered' },
] as const;
export type SkipReason = (typeof SKIP_REASONS)[number]['id'];

/** A short, stable fingerprint of the approved text (FNV-1a). Not security; it only tells two versions apart. */
export function versionOf(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}
